import {
  useContext,
  useEffect,
  useState,
  type SetStateAction,
  type Dispatch,
} from "react";
import {
  FeaturesFormSchema,
  type FeaturesFormType,
  VinlyCoverLinerFeaturesFormSchema,
} from "./FeaturesFormSchema";
import {
  type featureOrderState,
  getFeatureValuesOrDefault,
  OrderContext,
  type OrderState,
} from "../../../../../../context/OrderContext";
import FeaturesPlaceholder from "../../../../../../sections/orders/FeaturesPlaceholder/FeaturesPlaceholder";
import OrderForm, { type GenericFieldValidationType } from "../../../OrderForm";
import "./FeaturesForm.scss";
import type {
  ObstructionTypeArray,
  SpecFormPropTypes,
  VinylCoveredStepsInfo,
} from "../../Spec/SpecFormSchema";
import { CoverFeature, type FeatureValuesType } from "../Feature/CoverFeature";
import { SameAsFloor, YES } from "../Feature/FeatureConstants";
import type { BaseDeckFormType } from "../Feature/FeatureSchema";
import { FeatureType, OrderStatus, OrderType } from "models";
import { LinerFeature } from "../Feature/LinerFeature";
import type { OrderActionType } from "context/OrderReducer";
import { getLathamSpec } from "components/orders/helpers/getLathamSpec";
import type { LabeledResources } from "sections/orders/Orders";
import LinerMaterialService from "sections/orders/LinerMaterialService";
import { useT } from "i18n";

interface FeaturesFormProps {
  data: FeaturesFormType;
  hasEditAccess: boolean;
}

export interface FeatureFormFieldValidationStatus {
  id: boolean;
  featureName: boolean;
  anchorTypeOptions: boolean;
  cableAssembly: boolean;
  isRemovable: boolean;
  stepFastener: boolean;
  stepComment: boolean;
  isCovered: boolean;
  stepMaterialOption: boolean;
  stepStripe: boolean;
  stripeWidth: boolean;
  differentTreads: boolean;
  riserMaterial: boolean;
  stepMaterial: boolean;
  notes: boolean;
  moldedStepFace: boolean;
  hasYStrap: boolean;
  yStrapQty: boolean;
  divingBoardFastenType: boolean;
  coverExtension: boolean;
  coverExtensionWidth: boolean;
}

export interface FeaturesFormFieldsValidationStatus {
  features: FeatureFormFieldValidationStatus[];
}
interface PageProps {
  props: FeaturesFormProps;
  orderContext: {
    state: OrderState;
    dispatch: React.Dispatch<OrderActionType>;
  };
}
/**
 * @todo dynamic features
 * @param props
 * @constructor
 */
const FeaturesForm = (props: FeaturesFormProps) => {
  const t = useT();
  const { state, dispatch } = useContext(OrderContext);
  const key = state?.features?.features?.map((feat) => feat.id)?.sort() ?? "";
  return (
    <FeaturesPage
      props={props}
      orderContext={{ state, dispatch }}
      key={key.toString()}
    />
  );
};
function FeaturesPage(pageProps: PageProps) {
  const t = useT();
  const props = pageProps.props;
  const { state, dispatch } = pageProps.orderContext;
  const featuresScanData = state.featuresData;
  const disabled = state?.order?.archived || !props?.hasEditAccess;
  const isReadyOrderForVinlyFeature =
    state?.order?.status === OrderStatus.ReadyForOrder &&
    state?.order?.type === OrderType.Liner;
  const isAfterQuote =
    state.order?.status === OrderStatus.QuotedOptions ||
    state.order?.status === OrderStatus.ReadyForOrder;

  useEffect(() => {
    // if no features are tagged then the yup validation will always be successfull
    // to prevent this from happening we require that isViewed is true and update that value once this component mounts
    updateOrderState();
  }, []);

  /**
   * All fields are "valid", until we get an error!
   */
  const initialFeaturesFormValidationStatus =
    (): FeaturesFormFieldsValidationStatus => {
      if (featuresScanData.length) {
        const features = featuresScanData.map((el) => {
          return {
            id: true,
            featureName: true,
            anchorTypeOptions: true,
            cableAssembly: true,
            isRemovable: true,
            stepFastener: true,
            stepComment: true,
            isCovered: true,
            stepMaterialOption: true,
            stepMaterial: true,
            stepStripe: true,
            stripeWidth: true,
            differentTreads: true,
            riserMaterial: true,
            notes: true,
            moldedStepFace: true,
            hasYStrap: true,
            yStrapQty: true,
            divingBoardFastenType: true,
            coverExtension: true,
            coverExtensionWidth: true,
          } as FeatureFormFieldValidationStatus;
        });
        return { features: features };
      }
      return { features: [] };
    };

  // Validation state
  const [validFields, setValidFields] = useState<FeaturesFormFieldsValidationStatus>(
    initialFeaturesFormValidationStatus(),
  );
  const [features, setFeatures] = useState<BaseDeckFormType[]>(
    getFeatureValuesOrDefault(state),
  );
  const [stepFasterOptions, setStepFastenerOptions] = useState<
    { label: string; value: string }[] | []
  >([]);

  const getCurrentFeatureNote = (feature: BaseDeckFormType) => {
    let notes = "";
    if (feature.featureType === FeatureType.MoldedStep && feature.moldedStepFace) {
      notes += `${feature.moldedStepFace}. `;
    }
    if (feature && feature.notes && feature.notes.length > 0) {
      const noteList = feature.notes.split(". ");
      noteList.forEach((note: string, index) => {
        if (note.length > 0) {
          notes += note;
          if (index != noteList.length - 1) {
            notes += ". ";
          }
        }
      });
    }
    return notes;
  };

  const getFeatureAttributes = (lathamSpec?: SpecFormPropTypes) => {
    if (!lathamSpec) return [];
    if (lathamSpec.coverFeatureAttributes) {
      lathamSpec.coverFeatureAttributes.obstructionTypeArray?.forEach(
        (info: ObstructionTypeArray, index) => {
          const currentFeature = features.filter(
            (feature) => feature.featureName === info.obstructionLabel,
          );
          if (currentFeature.length > 0) {
            info.obstructionComments = getCurrentFeatureNote(currentFeature[0]);
          }
        },
      );
      return lathamSpec.coverFeatureAttributes;
    }
    return [];
  };

  const getVinylCoveredStepsInfoSpec = (lathamSpec?: SpecFormPropTypes) => {
    if (!lathamSpec) return [];
    const vinlyCoverFeatures = features.filter(
      (feature) => feature.featureType === FeatureType.VinylFeature,
    );
    lathamSpec.vinylCoveredSteps?.vos?.forEach(
      (stepsInfo: VinylCoveredStepsInfo, index) => {
        stepsInfo.stepMaterial =
          vinlyCoverFeatures[index]?.stepMaterialOption === SameAsFloor
            ? state.spec.linerMaterialAttributes.materialFloor
            : vinlyCoverFeatures[index]?.stepMaterial;
        stepsInfo.stepMaterialOption =
          vinlyCoverFeatures[index]?.stepMaterialOption ?? "";
        stepsInfo.stepComment =
          vinlyCoverFeatures[index]?.stepComment?.replace("Vinyl Feature", "VF") ?? "";
        stepsInfo.stepComment || vinlyCoverFeatures[index]?.notes;
        stepsInfo.stepFastener = vinlyCoverFeatures[index]?.stepFastener ?? "";
        stepsInfo.whiteStripeTread = vinlyCoverFeatures[index]?.stepStripe === YES;
        stepsInfo.colorStripeWidth = Number(vinlyCoverFeatures[index]?.stripeWidth);
      },
    );
    return lathamSpec.vinylCoveredSteps;
  };

  const getFullPerimeterCoverAccessoryAttributesSpec = (
    lathamSpec: SpecFormPropTypes,
  ) => {
    const totalYStrapQty = features.reduce((total, feature) => {
      if (feature.hasYStrap && feature.yStrapQty) {
        return total + Number(feature.yStrapQty);
      }
      return total;
    }, 0);

    lathamSpec.fullPerimeterCoverAccessoryAttributes = {
      ...lathamSpec.fullPerimeterCoverAccessoryAttributes,
      yStrapAssembly:
        totalYStrapQty > 0
          ? {
              yStrapFeatureId: "F780_45",
              yStrapQty: Number(totalYStrapQty) ?? 0,
            }
          : { yStrapFeatureId: "", yStrapQty: 0 },
    };

    return lathamSpec.fullPerimeterCoverAccessoryAttributes;
  };

  const lathamSpec = getLathamSpec(state);
  const specVals = {
    ...lathamSpec,
    vinylCoveredSteps: getVinylCoveredStepsInfoSpec(lathamSpec),
    coverFeatureAttributes: getFeatureAttributes(state.spec),
    fullPerimeterCoverAccessoryAttributes:
      getFullPerimeterCoverAccessoryAttributesSpec(lathamSpec),
  };

  const updateOrderState = () => {
    dispatch({
      type: "updateFeatures",
      payload: { features: features, isViewed: true } as featureOrderState,
    });
    dispatch({
      type: "updateSpec",
      payload: specVals as SpecFormPropTypes,
    });
  };

  const setFeature = (feature: BaseDeckFormType) => {
    setFeatures((featuresToSet: BaseDeckFormType[]) =>
      featuresToSet.length == 0
        ? [feature]
        : featuresToSet.map((oldFeature) =>
            feature.id == oldFeature.id ? feature : oldFeature,
          ),
    );
  };

  const getFieldNotes = (feature: LabeledResources, index: number) => {
    let appNote = "";
    const newFeature = features[index];
    if (newFeature && newFeature.notes && newFeature.notes.length > 0) {
      const noteList = newFeature.notes.split(". ");
      if (noteList.length == 2) {
        appNote = noteList[0];
      } else {
        appNote = noteList[0];
      }
    }
    return appNote;
  };

  const getFeatureNotes = (feature: LabeledResources, index: number) => {
    let portalNote = "";
    const newFeature = features[index];

    if (newFeature && newFeature.notes && newFeature.notes.length > 0) {
      const noteList = newFeature.notes.split(". ");
      if (noteList.length == 2) {
        portalNote = noteList[1];
      }
    }
    return portalNote;
  };

  useEffect(() => {
    if (!features.length) {
      dispatch({
        type: "updateFeatures",
        payload: { isValid: true } as featureOrderState,
      });
    }
  }, [features]);

  useEffect(() => {
    if (
      state.order?.type === OrderType.Liner ||
      state.project?.type === OrderType.Liner
    ) {
      const getStepFasteners = async () => {
        const data = await LinerMaterialService.getStepFasteners();
        const options = data.getINP15DataOut.INP15List.INP15Record.map((el: any) => ({
          label: el["@_description"],
          value: el["@_description"],
        }));
        setStepFastenerOptions(options);
      };
      getStepFasteners();
    }
  }, []);

  return (
    <div className={"features-form"}>
      <div className={"features-form__title"}>
        {t("component.forms.features.title", "Features")}
      </div>
      {featuresScanData.length && features.length ? (
        <OrderForm
          onFieldsInvalid={updateOrderState}
          onFieldsValid={updateOrderState}
          values={{ features: features } as unknown as FeaturesFormType}
          schema={
            !isAfterQuote ? FeaturesFormSchema : VinlyCoverLinerFeaturesFormSchema
          }
          setValidFields={
            setValidFields as Dispatch<SetStateAction<GenericFieldValidationType>>
          }
          initialValidationState={initialFeaturesFormValidationStatus()}
        >
          {featuresScanData.map((feature, i) => (
            <span key={i}>
              {state.project?.type === OrderType.Cover ? (
                <CoverFeature
                  name={feature.featureName}
                  key={`feature-form-${i}`}
                  title={feature.label}
                  type={feature.type}
                  notes={feature.notes}
                  feature={features[i] as FeatureValuesType}
                  measurement={feature?.measurement}
                  project={state?.project}
                  validFields={validFields.features[i]}
                  setFeature={(feature) => {
                    setFeature(feature);
                  }}
                  fieldNotes={getFieldNotes(feature, i)}
                  featureNotes={getFeatureNotes(feature, i)}
                  images={feature.images}
                  archived={disabled}
                  divingBoardFastenType={feature.divingBoardFastenType}
                  hasYStrap={features[i]?.hasYStrap ?? false}
                  yStrapQty={features[i]?.yStrapQty?.toString()}
                  coverExtension={features[i]?.coverExtension}
                  coverExtensionWidth={features[i].coverExtensionWidth}
                  coverExtensionStrapLength={features[i].coverExtensionStrapLength}
                />
              ) : (
                <LinerFeature
                  name={feature.featureName}
                  key={`feature-form-${i}`}
                  title={feature.label}
                  type={feature.type}
                  feature={features[i] as FeatureValuesType}
                  measurement={feature?.measurement}
                  project={state?.project}
                  linerMaterials={state?.linerMaterials}
                  stepFasterOptions={stepFasterOptions}
                  validFields={validFields.features[i]}
                  setFeature={(feature) => {
                    setFeature(feature);
                  }}
                  notes={feature.notes}
                  fieldNotes={getFieldNotes(feature, i)}
                  featureNotes={getFeatureNotes(feature, i)}
                  moldedStepFace={feature.moldedStepFace}
                  images={feature.images}
                  archived={disabled}
                  brand={state.product.brand}
                  isAfterQuote={isAfterQuote}
                  isReadyOrderForVinlyFeature={isReadyOrderForVinlyFeature}
                />
              )}
              {i < featuresScanData.length - 1 && (
                <hr className={"features-form__hr"} />
              )}
            </span>
          ))}
        </OrderForm>
      ) : (
        <FeaturesPlaceholder />
      )}
    </div>
  );
}

export default FeaturesForm;
